import { css, cx } from "@emotion/css";
import { Tooltip } from "@octopusdeploy/design-system-components";
import { borderRadius, fontWeight, lineHeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { LiveStatusSummaryResource, TimelineItem } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { LiveStatusIcon } from "~/areas/projects/components/Observability/LiveStatusIcon";
import { StatusDisplayLookup } from "~/areas/projects/components/Observability/StatusDisplayLookup";
import { SyncStatusIcon } from "~/areas/projects/components/Observability/SyncStatusIcon";
import TaskStatusIcon from "~/areas/projects/components/TaskStatusIcon/index";
import FeatureToggleVisibility from "~/components/FeatureToggle/New/FeatureToggleVisibility";
import InternalLink from "~/components/Navigation/InternalLink/index";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import DateFormatter from "~/utils/DateFormatter";
import { liveStatusSummaryText } from "./liveStatusCalculators";
interface LiveStatusSummaryBarProps {
    spaceId: string;
    projectSlug: string;
    liveStatusSummary: LiveStatusSummaryResource | null;
    latestDeployment: TimelineItem | null;
}
export function LiveStatusSummaryBar({ spaceId, projectSlug, liveStatusSummary, latestDeployment }: LiveStatusSummaryBarProps) {
    const isNewStatusIconsFeatureEnabled = useOctopusFeatureToggle("new-status-icons", false);
    const iconSize = isNewStatusIconsFeatureEnabled ? 40 : 48;
    const gridHeight = isNewStatusIconsFeatureEnabled ? "22px" : "24px";
    function LiveStatusContent() {
        if (!liveStatusSummary) {
            return <></>;
        }
        return (<div data-chameleon-tour="application-live-status" className={cx(styles.iconStatusCard(gridHeight))}>
                <div className={styles.iconStatusCardIcon}>
                    <LiveStatusIcon status={liveStatusSummary.HealthStatus} size={iconSize}/>
                </div>
                <div className={styles.iconStatusCardTitle}>{liveStatusSummary.HealthStatus}</div>
                <div className={styles.iconStatusCardStatus}>Last updated {DateFormatter.momentAgo(liveStatusSummary.LastUpdated.toString())}</div>
            </div>);
    }
    function LastDeploymentContent() {
        if (!latestDeployment) {
            return <></>;
        }
        const deploymentStatusText = !latestDeployment.Completed ? <span>Started {DateFormatter.momentAgo(latestDeployment.Created.toString())}</span> : <span>Completed {DateFormatter.momentAgo(latestDeployment.Completed?.toString() ?? "")}</span>;
        return (<InternalLink to={links.deploymentPage.generateUrl({ spaceId, projectSlug, deploymentId: latestDeployment.Id, releaseVersion: latestDeployment.Version })}>
                <div data-chameleon-tour="last-deployment" className={cx(styles.iconStatusCard(gridHeight), styles.truncatedGrid)}>
                    <div className={styles.iconStatusCardIcon}>
                        <TaskStatusIcon size={iconSize} item={{ PendingInterruptionTypes: latestDeployment.PendingInterruptionTypes, HasWarningsOrErrors: latestDeployment.HasWarningsOrErrors, IsCompleted: latestDeployment.IsCompleted, State: latestDeployment.TaskState }}/>
                    </div>
                    <Tooltip content={`${latestDeployment.Version} ${latestDeployment.Iteration}`}>
                        <div className={cx(styles.iconStatusCardTitle, styles.truncatedText)}>
                            {latestDeployment.Version} {latestDeployment.Iteration}
                        </div>
                    </Tooltip>
                    <div className={styles.iconStatusCardStatus}>{deploymentStatusText}</div>
                </div>
            </InternalLink>);
    }
    function SyncStatusContent() {
        if (!liveStatusSummary) {
            return <></>;
        }
        return (<div className={cx(styles.iconStatusCard(gridHeight))}>
                <div className={styles.iconStatusCardIcon}>
                    <SyncStatusIcon status={liveStatusSummary.SyncStatus} size={iconSize}/>
                </div>
                <div className={styles.iconStatusCardTitle}>{StatusDisplayLookup[liveStatusSummary.SyncStatus]}</div>
                <div className={styles.iconStatusCardStatus}>Last updated {DateFormatter.momentAgo(liveStatusSummary.LastUpdated.toString())}</div>
            </div>);
    }
    function StatusSummaryContent() {
        if (!latestDeployment || !liveStatusSummary) {
            return <></>;
        }
        return <span>{liveStatusSummaryText(latestDeployment.TaskState, liveStatusSummary.HealthStatus, liveStatusSummary.SyncStatus)}</span>;
    }
    return (<div className={styles.summaryBar}>
            <LiveStatusCard title="Health Status">{LiveStatusContent()}</LiveStatusCard>
            <FeatureToggleVisibility toggle="verify-argocd-application-health">
                <LiveStatusCard title="Sync Status">{SyncStatusContent()}</LiveStatusCard>
            </FeatureToggleVisibility>
            <LiveStatusCard title={latestDeployment?.IsCompleted ? "Last Deployment" : "Current Deployment"}>{LastDeploymentContent()}</LiveStatusCard>
            <LiveStatusCard title="Status Summary">{StatusSummaryContent()}</LiveStatusCard>
        </div>);
}
interface LiveStatusCardProps {
    title: string;
    children: JSX.Element;
}
export function LiveStatusCard(props: LiveStatusCardProps) {
    return (<div className={styles.liveStatusCard}>
            <div className={styles.liveStatusCardTitle}>{props.title}</div>
            <div>{props.children}</div>
        </div>);
}
const styles = {
    summaryBar: css({
        background: themeTokens.color.background.secondary.default,
        display: "flex",
        flexDirection: "row",
        marginBottom: space[8],
    }),
    liveStatusCard: css({
        background: themeTokens.color.background.primary.default,
        flex: 1,
        height: "120px",
        borderRadius: borderRadius.large,
        margin: space[16],
        padding: space[16],
        boxShadow: themeTokens.shadow.small,
    }),
    liveStatusCardTitle: css({
        font: text.heading.xSmall,
        lineHeight: lineHeight.xSmall,
        color: themeTokens.color.text.secondary,
        fontWeight: fontWeight[700],
        textTransform: "uppercase",
        marginBottom: space[12],
    }),
    iconStatusCard: (gridHeight: string) => css({
        display: "grid",
        gridTemplateColumns: "60px auto",
        gridTemplateRows: `${gridHeight} 24px`,
    }),
    iconStatusCardIcon: css({
        gridRowStart: 1,
        gridRowEnd: 2,
    }),
    iconStatusCardTitle: css({
        gridRow: 1,
        gridColumn: 2,
        font: text.body.bold.large,
        color: themeTokens.color.text.primary,
    }),
    iconStatusCardStatus: css({
        gridRow: 2,
        gridColumn: 2,
        font: text.body.regular.medium,
        color: themeTokens.color.text.secondary,
    }),
    truncatedGrid: css({
        gridTemplateColumns: "60px minmax(0, 1fr)",
    }),
    truncatedText: css({
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    }),
};
